(ns storrito.editor.quiz-sticker
  (:require ["fabric" :refer [fabric]]
            [clojure.string :as str]
            [storrito.editor.poll-v2-sticker :as poll-v2-sticker]))

;; Concept:
;;
;; Creates a FabricJS object that looks like an Instagram quiz
;; sticker.

(def quiz-width 770)

(def +min-quiz-font-size+ 62.0)
(def +max-quiz-font-size+ 85.0)

(defn set-background-gradient
  [fabric-element color-1 color-2]
  (.set fabric-element
        "fill"
        (js/fabric.Gradient.
          #js{:coords #js{:x1 0
                          :y1 0
                          :x2 quiz-width
                          :y2 0}
              :type "linear"
              :gradientUnits "pixels"
              :colorStops #js[#js{:offset 0 :color color-1}
                              #js{:offset 1 :color color-2}]})))

(defn option-group
  [{:keys [idx tag text top]}]
  (let [panel (js/fabric.Rect. #js{:width (- quiz-width 97)
                                   :rx 64
                                   :ry 64
                                   :height 130
                                   :originX "center"
                                   :originY "center"
                                   :stroke "#555555"
                                   :fill "#ffffff"})

        tag-circle (js/fabric.Rect. #js{:rx 32
                                        :ry 32
                                        :width 64
                                        :height 64
                                        :left -266
                                        :originX "center"
                                        :originY "center"
                                        :stroke "#000000"
                                        :strokeWidth 3
                                        :fill "#ffffff"})

        tag (js/fabric.Textbox. tag
                                #js{:fill "#000000"
                                    :originX "center"
                                    :originY "center"
                                    :width 64
                                    :height 64
                                    :left -266
                                    :fontSize 32
                                    :fontFamily "Roboto"
                                    :fontWeight "bold"
                                    :textAlign "center"})
        text (poll-v2-sticker/fit-lines-textbox text
                                                {:fill "#000000"
                                                 :breakWords true
                                                 :originX "center"
                                                 :originY "center"
                                                 :width 531
                                                 :left 64
                                                 :fontSize 32
                                                 :fontFamily "AvenyT"
                                                 :textAlign "left"}
                                                2)
        ]
    (js/fabric.Group. (into-array [panel tag-circle tag text])
                      #js{:originX "left"
                          :originY "top"
                          :top (+ top (* idx
                                         168))})))

(defn quiz-sticker
  "Builds the fabricjs representation of an Instagram question sticker"
  [params]
  ;; Bugfix for wrong text bounding boxes
  ;; Due to wrong calculated char width in cache, the cache needs to be cleared
  ;; Found at: https://github.com/kangax/fabric.js/issues/2612
  (js/fabric.util.clearFabricFontCache)
  (let [options (:options params)
        min-panel-height 530
        text-color (:text_color params)
        shadow #js{:color "rgba(0, 0, 0, 0.5)",
                   :blur 5,
                   :offsetX 3,
                   :offsetY 3,
                   :affectStroke false}
        answer-font-size (fn [s]
                           (let [char-count (count s)
                                 line-count (count (str/split-lines s))]
                             (cond
                               (= line-count 2) (* 0.80 +max-quiz-font-size+)
                               (< char-count 8) +max-quiz-font-size+
                               (< char-count 10) (* 0.85 +max-quiz-font-size+)
                               (< char-count 12) (* 0.70 +max-quiz-font-size+)
                               (< char-count 14) +min-quiz-font-size+
                               :else +min-quiz-font-size+)))

        question-text (poll-v2-sticker/fit-lines-textbox (:question params)
                                                         {:fill "#ffffff"
                                                          :fontSize (answer-font-size (:question params))
                                                          :breakWords true
                                                          :originX "center"
                                                          :originY "top"
                                                          :width (- quiz-width 80)
                                                          :top 44
                                                          :fontFamily "AvenyT"
                                                          :fontWeight "bold"
                                                          :textAlign "center"}
                                                         3)

        question-height (.-height question-text)
        panel-object (js/fabric.Rect. #js{:fill "#ffffff"
                                          :width quiz-width
                                          :rx 42
                                          :ry 42
                                          :height (+ min-panel-height
                                                     (- question-height
                                                        61)
                                                     (* (- (count options) 2)
                                                        168))
                                          :originX "center"
                                          :originY "top"
                                          :shadow shadow})

        question-panel-1 (js/fabric.Rect. #js{:width quiz-width
                                              :height 137
                                              :rx 42
                                              :ry 42
                                              :originX "center"
                                              :originY "top"})
        question-panel-2 (js/fabric.Rect. #js{:width quiz-width
                                              :height (+ 19 question-height)
                                              :originX "center"
                                              :originY "top"
                                              :top 72
                                              })
        question-group (do (set-background-gradient question-panel-1
                                                    (:start_background_color params)
                                                    (:end_background_color params))
                           (set-background-gradient question-panel-2
                                                    (:start_background_color params)
                                                    (:end_background_color params))
                           (js/fabric.Group. (into-array [question-panel-1 question-panel-2 question-text])
                                             #js{:originX "left"
                                                 :originY "top"}))

        options-group (js/fabric.Group. (into-array
                                          (map-indexed
                                            (fn [idx option]
                                              (option-group {:idx idx
                                                             :tag (get ["A" "B" "C" "D"]
                                                                       idx)
                                                             :text option
                                                             :top (+ 139 question-height)}))
                                            options))
                                        #js{:originX "left"
                                            :originY "top"})]

    (js/fabric.Group. (into-array [panel-object question-group options-group])
                      #js{:id "quiz-sticker"
                          :lockScalingFlip true
                          :max-scale 1.3
                          :min-scale 0.3
                          :quiz #js{:question (:question params)
                                    :options (clj->js (:options params))
                                    :correct_answer (:correct_answer params)
                                    :start_background_color (:start_background_color params)
                                    :end_background_color (:end_background_color params)
                                    :text_color text-color}
                          :top (or (:top params) 10)
                          :left (or (:left params) 10)
                          :angle (or (:angle params) 0.0)
                          :scaleY (or (:scaleX params) 1.0)
                          :scaleX (or (:scaleY params) 1.0)})))
